import { isAllTasksEnabledForOrg } from '@/constants/feature-flags';
import { useFeatureFlagState } from '@/hooks/useFeatureFlag';
import { useWorkforceEligiblePrograms } from '@/hooks/useShouldShowWorkforceConsole';
import { useMyManagedTeams } from '@/pages/workforce/hooks/useMyManagedTeams';
import { useStore } from '@/store/store';

/**
 * Global dynconfig flag (`feature_flag` namespace) that switches All Tasks to
 * permission-based access. Separate from the Workforce Console's
 * `permissions_based_wc_access`, so flipping one never changes the other.
 */
const PERMISSIONS_BASED_ACCESS_FLAG = 'permissions_based_all_tasks_access';

/**
 * Which access rule All Tasks follows: `permissionsBased` is true only when
 * `permissions_based_all_tasks_access` is explicitly on, so an absent flag, a
 * failed read and the first load all mean the team-lead rule. `enabled: false`
 * reads nothing.
 */
export const useAllTasksAccessMode = (
  enabled = true,
): { permissionsBased: boolean; isLoading: boolean } => {
  const { enabled: on, isLoading } = useFeatureFlagState(
    enabled ? PERMISSIONS_BASED_ACCESS_FLAG : undefined,
  );
  return { permissionsBased: on, isLoading };
};

/**
 * Whether the All Tasks page should be shown in the sidebar and reachable by
 * URL. Shared by the sidebar entry and the route guard so the two never disagree.
 *
 * The page shows to the same people as the Workforce Console, under its own
 * mode flag:
 *   - the selected org must be in `REACT_APP_ALL_TASKS_ENABLED_ORG_IDS`; any
 *     other org reads nothing and is hidden;
 *   - `permissions_based_all_tasks_access` off or absent: team leads of a team
 *     in the selected org (`useMyManagedTeams`);
 *   - flag on: users whose permission map grants the console APIs in at least
 *     one program of the org (`useWorkforceEligiblePrograms`, which shares its
 *     cache with the console).
 *
 * Nothing is shown while the flag or checks load. UI gate only:
 * ListOrganizationManualTasks scopes its results by permission on its own.
 */
export const useShouldShowAllTasks = (): { shouldShow: boolean; isLoading: boolean } => {
  const organizationId = useStore(state => state.selectedOrganizationId);
  const orgEnabled = isAllTasksEnabledForOrg(organizationId);
  const mode = useAllTasksAccessMode(orgEnabled);
  const leads = useMyManagedTeams({
    enabled: orgEnabled && !mode.isLoading && !mode.permissionsBased,
  });
  const eligible = useWorkforceEligiblePrograms(orgEnabled && mode.permissionsBased);

  if (!orgEnabled) return { shouldShow: false, isLoading: false };
  if (mode.isLoading) return { shouldShow: false, isLoading: true };
  if (!mode.permissionsBased) {
    return { shouldShow: leads.isManagerOfAny, isLoading: leads.isLoading };
  }
  return { shouldShow: eligible.programs.length > 0, isLoading: eligible.isLoading };
};
